import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { TrendingUp, ShoppingBag, Banknote, QrCode, Download, CalendarRange } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { http, rupiah, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function Reports() {
  const [r, setR] = useState(null);
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today());
  const [dl, setDl] = useState(false);

  const load = useCallback(() => {
    http.get(`/admin/reports?start=${start}&end=${end}`).then(({ data }) => setR(data)).catch(() => {});
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  async function download() {
    setDl(true);

    try {
      const res = await http.get(
        `/admin/reports/export?start=${start}&end=${end}`,
        { responseType: "blob" }
      );

      const contentType =
        res.headers?.["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const url = URL.createObjectURL(
        new Blob([res.data], {
          type: contentType,
        })
      );

      const disposition =
        res.headers?.["content-disposition"] || "";

      const filenameMatch =
        disposition.match(
          /filename="?([^"]+)"?/i
        );

      const filename =
        filenameMatch?.[1] ||
        `laporan-${start}_sd_${end}.xlsx`;

      const a =
        document.createElement("a");

      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);

      toast.success(
        "Laporan Excel berhasil diunduh"
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDl(false);
    }
  }

  const cards = r ? [
    { label: "Total Revenue", value: rupiah(r.revenue), Icon: TrendingUp },
    { label: "Pesanan Lunas", value: `${r.paid_orders}/${r.total_orders}`, Icon: ShoppingBag },
    { label: "Revenue Cash", value: rupiah(r.cash_revenue), Icon: Banknote },
    { label: "Revenue QRIS", value: rupiah(r.qris_revenue), Icon: QrCode },
  ] : [];
  const daily = (r?.daily || []).map((d) => ({ ...d, day: new Date(d.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) }));
  const top = r?.top_products || [];

  return (
    <AdminShell>
      <PageHead eyebrow="Analitik" title="Laporan" desc="Filter tanggal & unduh laporan Excel.">
        <button data-testid="export-report-btn" onClick={download} disabled={dl} className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-50"><Download size={16} /> Unduh Excel</button>
      </PageHead>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
        <CalendarRange size={18} className="text-primary" />
        <label className="text-xs font-bold text-muted-foreground">Dari<input data-testid="report-start" type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="ml-2 rounded-lg border border-border bg-muted px-2 py-1.5 text-sm text-card-foreground outline-none" /></label>
        <label className="text-xs font-bold text-muted-foreground">Sampai<input data-testid="report-end" type="date" value={end} min={start} max={today()} onChange={(e) => setEnd(e.target.value)} className="ml-2 rounded-lg border border-border bg-muted px-2 py-1.5 text-sm text-card-foreground outline-none" /></label>
        <div className="flex gap-2">
          <button onClick={() => { setStart(daysAgo(6)); setEnd(today()); }} className="rounded-lg bg-muted px-3 py-1.5 text-xs font-bold text-card-foreground">7 Hari</button>
          <button onClick={() => { setStart(daysAgo(29)); setEnd(today()); }} className="rounded-lg bg-muted px-3 py-1.5 text-xs font-bold text-card-foreground">30 Hari</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map(({ label, value, Icon }) => (
          <div key={label} className="rounded-3xl border border-border bg-card p-5">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Icon size={18} /></div>
            <div className="mt-3 text-xs font-semibold text-muted-foreground">{label}</div>
            <div className="mt-1 font-heading text-xl font-extrabold tracking-tight text-card-foreground">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-5">
          <b className="font-heading text-lg text-card-foreground">Revenue per Hari</b>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tickFormatter={(v) => `${v / 1000}k`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={36} />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--popover-foreground))" }} cursor={{ fill: "hsl(var(--muted))" }} />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-3xl border border-border bg-card p-5">
          <b className="font-heading text-lg text-card-foreground">Menu Terlaris</b>
          <div className="mt-4 h-64">
            {top.length === 0 ? <div className="grid h-full place-items-center text-sm text-muted-foreground">Belum ada data pada rentang ini.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => `${v} porsi`} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--popover-foreground))" }} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="qty" radius={[0, 8, 8, 0]} maxBarSize={26}>{top.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
